import { IonIcon, IonSpinner, useIonToast } from '@ionic/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ICasinoSearchData, IState, ITabProperties } from 'appRedux/createStore';
import { CasinoService, IRequestDetailParam, IRequestTrendsParam } from 'services/casino';
import { debounce } from 'lodash';
import { of } from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';
import {
  convertToSlotData,
  getCleanGameName,
  getCleanThemeName,
  toCapitalizedWords,
  FilterTypeEnum,
  IFilterType,
  OrderByEnum,
  TimeFrameValueEnum,
} from 'common/common';
import TextMarquee from 'components/TextMarquee';
import useSlotDetailHistory from 'globalServices/useSlotDetailHistory';
import { close, ellipsisVertical, swapVerticalOutline } from 'ionicons/icons';
import { ReactSVG } from 'react-svg';
import {
  ICasinoReport,
  ISearchInfo,
  ISlotGraphData,
  ITrendsReport,
} from 'appRedux/models/casinoModels';
import { isIncludedInFavorites } from 'pages/Favorites/components/LikeButton';
import useFavorites from 'common/reactHooks/useFavorites';
import usePlaylist, { isIncludedInPlaylist } from 'common/reactHooks/usePlaylist';
import { PlaylistIcon } from 'components/PlaylistIcon/PlaylistIcon';
import { FavoriteIcon } from 'components/FavoriteIcon/FavoriteIcon';
import { selectUserId, selectFavoritesList, selectPlaylist } from 'appRedux/selectors';
import { featureFlags } from 'config/featureFlags';
import { SlotReduxCommandCreator } from 'appRedux/actions/slotCommandCreator';
import {
  MACHINE_MENU_POPOVER_WIDTH,
  machineMenuBackdropStyle,
  machineMenuContainerStyle,
  machineMenuDividerStyle,
  machineMenuFilterItemStyle,
  machineMenuIconSlotStyle,
  machineMenuItemStyle,
} from 'components/MachineActionsMenu/menuStyles';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  ReferenceLine,
  Customized,
} from 'recharts';
import moment from 'moment';
import { formatBucketLabel, GraphBucketType, pickTickBuckets } from 'common/graphBuckets';
import { formatYAxisTick, isYInView, jackpotBarLayout } from 'common/yZoom';
import { INSIGHT_DEFAULT_TIMEFRAME, INSIGHT_TIMEFRAMES, resolveInsightTimeframe } from 'config/insightTimeframes';
import {
  getEgmDetailGraphDefaultTimeframe,
  resolveEgmDetailGraphDefaultTimeframeId,
} from 'lib/applicationConfig';
import { isInsightCasinoSchema } from 'services/insightCasino';
import { shouldShowPop } from 'lib/showPop';
import {
  parseValue,
  parseValueForTrends,
  TRENDS_EMPTY_VALUE,
  ntileGradients,
  ntileMenuColors,
  ntileTextColors,
  getRowColorWithOpacity,
} from 'common/include';
import { heatBeanRatings } from 'config/HeatBeanRatings';
import { HeatBeanObjectKey } from 'config/config';
import { InsightValueEnum } from 'config/Insights';
import SVGComponent from 'components/SmartLoading/SVGComponent';
import GameInformation from './components/Detail/GameInformation';
import SectionHeader from './components/Detail/SectionHeader';
import GradientLineChart from './components/Detail/GradientLineChart';
import { ISelectedSlots } from './DetailReport';
import {
  egmCardMeta,
  egmDetailId,
  egmDetailKey,
  insightCardName,
  insightCardTitle,
  isInsightCardRow,
  searchRowDetailId,
  withInsightRtp,
} from 'services/insightCasino';
import { useYAxisZoom } from 'hooks/useYAxisZoom';

const getRowColor = (ntile: number): string =>
  ntile >= 1 && ntile <= 5
    ? ntileGradients[ntile]
    : (heatBeanRatings[ntile as HeatBeanObjectKey]?.color ?? 'transparent');

const getMenuBackgroundColor = (ntile: number, jackpots: number): string =>
  jackpots > 0 ? '#FFD700' : (ntileMenuColors[ntile] ?? '#FFFFFF');

const getTextColor = (ntile: number): string => ntileTextColors[ntile] ?? '#000000';

const V2_TIME_TABS = [
  { label: '30 day', timeframeId: 6, value: TimeFrameValueEnum.months1 },
  { label: '7 day', timeframeId: 5, value: TimeFrameValueEnum.weeks1 },
  { label: '24h', timeframeId: 4, value: TimeFrameValueEnum.hours24 },
  { label: '8h', timeframeId: 3, value: TimeFrameValueEnum.hours8 },
  { label: '4h', timeframeId: 2, value: TimeFrameValueEnum.hours4 },
];

const V2_TREND_ROWS = [
  { insight: InsightValueEnum.return, label: 'RTP' },
  { insight: InsightValueEnum.jackpot_value, label: 'JKPT' },
  { insight: InsightValueEnum.money_won, label: '$ Won' },
  { insight: InsightValueEnum.money_played, label: '$ Played' },
  { insight: InsightValueEnum.spins, label: 'Spins' },
  { insight: InsightValueEnum.avg_money_won_per_spin, label: '$ W/S' },
];

const TRENDS_INSIGHT_FILTER = '{1,2,3,4,5,7,8,12,13}';

type InsightSlot = InsightValueEnum | null;

const CHART_INSIGHTS: { value: InsightValueEnum; label: string; color: string }[] = [
  { value: InsightValueEnum.return,                label: 'RTP',      color: '#F9379D' },
  { value: InsightValueEnum.jackpot_value,         label: 'Jackpots', color: '#FFD700' },
  { value: InsightValueEnum.money_won,             label: '$ Won',    color: '#11E162' },
  { value: InsightValueEnum.money_played,          label: '$ Played', color: '#FF3D51' },
  { value: InsightValueEnum.spins,                 label: 'Popular',  color: '#C5CED4' },
  { value: InsightValueEnum.avg_money_won_per_spin,label: '$ W/S',    color: '#11E162' },
  { value: InsightValueEnum.jackpot_value_per_spin,label: 'J W/S',    color: '#FFD700' },
  { value: InsightValueEnum.non_jkpt_won_per_spin, label: 'W/S-J',   color: '#deb084' },
];

const INSIGHT_STROKE_GRADIENTS: Partial<Record<InsightValueEnum, string>> = {
  [InsightValueEnum.return]:                 'drv2RtpLineGrad',
  [InsightValueEnum.money_won]:              'drv2WonGrad',
  [InsightValueEnum.money_played]:           'drv2PlayedGrad',
  [InsightValueEnum.spins]:                  'drv2SpinsGrad',
  [InsightValueEnum.avg_money_won_per_spin]: 'drv2WsGrad',
  [InsightValueEnum.jackpot_value_per_spin]: 'drv2JwsGrad',
};

const THEMED_TIME_TAB_INSIGHTS = new Set<InsightValueEnum>([
  InsightValueEnum.jackpot_value,
  InsightValueEnum.return,
]);

const INSIGHT_PRESETS: { label: string; primary: InsightValueEnum; secondary: InsightValueEnum; color: string }[] = [
  { label: '$ Won vs\n$ Played', primary: InsightValueEnum.money_won, secondary: InsightValueEnum.money_played, color: '#11e162' },
  { label: 'RTP vs\nPopular', primary: InsightValueEnum.return, secondary: InsightValueEnum.spins, color: '#F9379D' },
  { label: 'RTP vs\nJackpots', primary: InsightValueEnum.return, secondary: InsightValueEnum.jackpot_value, color: '#FFBD60' },
];


interface IDetailReportV2 {
  onBackSlotList: () => void;
  setSelectedSlotAction: (selectedData: ICasinoReport | null) => void;
  onStatsRefChange: (refValue: HTMLDivElement | null) => void;
}

const getHeatColor = (ntile: number | undefined) => {
  if (!ntile || !heatBeanRatings[ntile as HeatBeanObjectKey]) return 'rgba(0,0,0,0.1)';
  return heatBeanRatings[ntile as HeatBeanObjectKey].color;
};

const formatXLabel = (bucket: string, timeframeId: number): string => {
  // UTC getters: insight series store casino wall-clock with a Z suffix.
  return timeframeId <= 4
    ? moment.utc(bucket).format('h A')
    : moment.utc(bucket).format('MMM D');
};


type InsightCategory = 'percent' | 'dollars' | 'count';

const getInsightCategory = (insight: InsightValueEnum): InsightCategory => {
  if (insight === InsightValueEnum.return || insight === InsightValueEnum.win_rate) return 'percent';
  if (insight === InsightValueEnum.spins || insight === InsightValueEnum.jackpots) return 'count';
  return 'dollars';
};

// Millions roll up to M with a single decimal — 1,000,000 reads "1M" and
// 1,100,000 reads "1.1M" rather than "1000k" / "1100k".
const abbreviateNumber = (val: number): string => {
  if (val < 1000) return String(Math.round(val));
  const thousands = Math.round(val / 1000);
  // Roll over to M as soon as the K figure would read "1000K".
  if (thousands < 1000) return `${thousands}K`;
  return `${Number((val / 1_000_000).toFixed(1))}M`;
};

// Tooltip values follow the same rule as the axis once they reach a million, so
// the hover never reads "$1,100,000" against a "1.1M" tick.
const formatTooltipValue = (val: number, insight: InsightValueEnum): string => {
  const category = getInsightCategory(insight);
  // Percent series reach the chart already scaled to percent units, so they
  // skip parseValue — it would multiply by 100 a second time.
  if (category === 'percent') return `${Math.round(val)}%`;
  if (Math.abs(val) >= 1_000_000) {
    const sign = val < 0 ? '-' : '';
    const prefix = category === 'dollars' ? '$' : '';
    return `${sign}${prefix}${abbreviateNumber(Math.abs(val))}`;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  return String(parseValue(val, insight));
};

// The graph opens on $ Won / $ Played; default timeframe from
// applicationConfigurations egmDetailGraphDefaultTimeframe (fallback months1).
const DEFAULT_CHART_INSIGHTS: [InsightSlot, InsightSlot] = [
  InsightValueEnum.money_won,
  InsightValueEnum.money_played,
];

// The jackpot overlay rides on whichever money insight is on the chart, so it
// only makes sense while $ Won or $ Played is selected — both are dollars, and
// the bars can share that axis without adding a scale of their own.
const JACKPOT_OVERLAY_HOST_INSIGHTS = new Set<InsightValueEnum>([
  InsightValueEnum.money_won,
  InsightValueEnum.money_played,
]);

// Carries the overlay's jackpot dollars into the tooltip without drawing a
// second series — the bars themselves come from the Customized layer.
const JACKPOT_OVERLAY_KEY = 'jackpot_overlay';

// Coin stack, drawn in currentColor so the button can tint it gold when the
// overlay is on and leave it the swap-arrow grey when it is off.
const JackpotStackIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <ellipse cx="12" cy="6" rx="7" ry="3" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M5 6v4.5c0 1.66 3.13 3 7 3s7-1.34 7-3V6"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
    <path
      d="M5 12.5V17c0 1.66 3.13 3 7 3s7-1.34 7-3v-4.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const DetailReportV2: React.FC<IDetailReportV2> = ({ onBackSlotList, setSelectedSlotAction }) => {
  const casinoSvc = useMemo(() => new CasinoService(), []);
  const [presentToast] = useIonToast();
  const { activeSlotNumber, clearSlotHistory } = useSlotDetailHistory();

  const casinoId = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema?.kpCasinoPk
  ) as number;

  const dispatch = useDispatch();
  const slotCommands = SlotReduxCommandCreator(dispatch);

  const userId = useSelector(selectUserId);
  const favoritesList = useSelector(selectFavoritesList);
  const playlist = useSelector(selectPlaylist);
  const casinoSearchData = useSelector<IState>(
    (state) => state.app.persistedState.casinoSearchData
  ) as ICasinoSearchData | undefined;
  const isPlaylistEnabled = featureFlags.playlist;

  const [popoverPosition, setPopoverPosition] = useState<{ x: number; y: number } | null>(null);

  const { fetchUserFavorites, handleInsertFavorite, handleDeleteFavorite } = useFavorites({});
  const { fetchUserPlaylist, handleInsertPlaylist, handleDeletePlaylist } = usePlaylist();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<ISelectedSlots | null>(null);
  // Insight casinos (casino object has insightInstance): INSIGHT_TIMEFRAMES
  // only (no current hour, no SLJ), 24h default.
  const isInsightSchema = useSelector((state: IState) =>
    isInsightCasinoSchema(state.app.persistedState.casinoSchema)
  );
  // Per-casino POP visibility (casino.showPOP = 1). Display-only: hides the POP
  // reference line on the RTP chart; the slot data itself is unchanged.
  const showPop = useSelector((state: IState) =>
    shouldShowPop(state.app.persistedState.casinoSchema)
  );
  // Explore's timeframe — same key as IndexedDB/memory GetCasinoInsights cache.
  // Graph pills (activeTimeframeId) must NOT drive this; only the graph refetches on pill change.
  const exploreTimeframeValue = useSelector<IState>(
    (state) =>
      (state.app.persistedState.filterState[FilterTypeEnum.hottestSlots as IFilterType] as ITabProperties)
        ?.timeframe
  ) as TimeFrameValueEnum | undefined;
  const exploreTimeframeId = useMemo(
    () =>
      isInsightSchema
        ? resolveInsightTimeframe(exploreTimeframeValue).id
        : resolveInsightTimeframe(exploreTimeframeValue ?? TimeFrameValueEnum.months1).id,
    [isInsightSchema, exploreTimeframeValue]
  );
  // Details GRAPH pills only (shared by every insight / compare pair): 30d / 7d /
  // 24h / 8h / 4h — never 1h. Trends keeps its own 1 Hr column via h1_*. Shared
  // INSIGHT_TIMEFRAMES is not mutated so Explore / Trends / Compare stay intact.
  const timeTabs = useMemo(
    () =>
      isInsightSchema
        ? INSIGHT_TIMEFRAMES.filter((tf) => tf.apiValue !== 'hours1')
            .slice()
            .reverse()
            .map((tf) => ({ label: tf.label, timeframeId: tf.id, value: tf.value }))
        : V2_TIME_TABS,
    [isInsightSchema]
  );
  // Re-read when GetApplicationConfigurations lands (non-persisted Redux).
  const applicationConfigurations = useSelector<IState>((s) => s.app.applicationConfigurations);
  const defaultTimeframeId = useMemo(() => {
    void applicationConfigurations;
    const tf = getEgmDetailGraphDefaultTimeframe();
    return resolveEgmDetailGraphDefaultTimeframeId(timeTabs, tf);
  }, [timeTabs, applicationConfigurations]);
  const [activeTimeframeId, setActiveTimeframeId] = useState(defaultTimeframeId);
  // False until the first slot-data request for this EGM settles, so the
  // "No details" empty state can't flash during the debounce before it starts.
  const [slotFetched, setSlotFetched] = useState(false);
  const [graphData, setGraphData] = useState<ISlotGraphData[]>([]);
  // casinoInsight graph: the API says whether bars are hourly or daily and
  // lists the bucket starts (oldest first). Legacy responses carry neither.
  const [graphBucketType, setGraphBucketType] = useState<GraphBucketType | null>(null);
  const [graphBuckets, setGraphBuckets] = useState<string[] | null>(null);
  const [isGraphLoading, setIsGraphLoading] = useState(false);
  const [trendsData, setTrendsData] = useState<ITrendsReport[]>([]);
  const [chartInsights, setChartInsights] = useState<[InsightSlot, InsightSlot]>(
    DEFAULT_CHART_INSIGHTS
  );
  const [pickingSlot, setPickingSlot] = useState<0 | 1 | null>(null);
  // null while the overlay is following its default — a tap pins it either way
  // for the rest of the visit to this EGM.
  const [jackpotOverlayPref, setJackpotOverlayPref] = useState<boolean | null>(null);
  const [isTrendsLoading, setIsTrendsLoading] = useState(false);
  const [trendCellWidth, setTrendCellWidth] = useState(115);
  const tableAreaRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const slotRowWrapRef = useRef<HTMLDivElement>(null);
  const [isSlotRowTucked, setIsSlotRowTucked] = useState(false);

  const fetchSlotData = useCallback(
    (slot: number) => {
      if (!slot) return;
      setIsLoading(true);
      setSelectedSlot(null);
      setSelectedSlotAction(null);
      // Insight: GetIndividualSlotReport → sharedInsightSummary(casinoId, exploreTimeframe)
      // reads IndexedDB/memory; network only on miss; then pickInsightSummaryRow(mnum).
      const params: IRequestDetailParam = {
        casinofk: casinoId,
        timeframe: exploreTimeframeId,
        insight: InsightValueEnum.return,
        slot,
        orderby: OrderByEnum.Desc,
      };
      casinoSvc.GetIndividualSlotReport(params).subscribe({
        next(resp) {
          setSlotFetched(true);
          if (resp?.DataSet?.[0]) {
            setSelectedSlot({ slotNumber: slot, slotData: resp.DataSet[0] });
            setSelectedSlotAction(resp.DataSet[0]);
          }
        },
        error() {
          presentToast({
            position: 'top',
            duration: 4000,
            color: 'danger',
            message: 'Failed to load slot data.',
          }).catch(() => null);
          setSlotFetched(true);
          setIsLoading(false);
        },
        complete() {
          setSlotFetched(true);
          setIsLoading(false);
        },
      });
    },
    [casinoSvc, casinoId, presentToast, setSelectedSlotAction, exploreTimeframeId]
  );

  const fetchGraphData = useCallback(
    (slot: number, timeframeId: number) => {
      if (!slot) return;
      setIsGraphLoading(true);
      const params: IRequestDetailParam = {
        casinofk: casinoId,
        timeframe: timeframeId,
        insight: InsightValueEnum.money_won,
        slot,
        orderby: OrderByEnum.Desc,
      };
      casinoSvc.GetGraphBucketSlotReport(params).subscribe({
        next(resp) {
          const r = resp as
            | { slotPlayData?: ISlotGraphData[]; bucketType?: string; buckets?: string[] }
            | undefined;
          setGraphData(r?.slotPlayData ?? []);
          setGraphBucketType(r && (r.bucketType === 'hour' || r.bucketType === 'day') ? r.bucketType : null);
          setGraphBuckets(r && Array.isArray(r.buckets) ? r.buckets : null);
        },
        error() {
          setIsGraphLoading(false);
        },
        complete() {
          setIsGraphLoading(false);
        },
      });
    },
    [casinoSvc, casinoId]
  );

  const fetchTrendsData = useCallback(
    (slot: number) => {
      // No slot to fetch for: clear the flag rather than leaving the spinner up,
      // since the loading state is now only cleared when the stream completes.
      if (!slot) {
        setIsTrendsLoading(false);
        return undefined;
      }
      setIsTrendsLoading(true);
      const params: IRequestTrendsParam = {
        casinoFk: casinoId,
        insightFilter: TRENDS_INSIGHT_FILTER,
        slotNumber: slot,
      };
      return casinoSvc
        .GetTrendsData(params)
        .pipe(
          // Trends is fetched once per EGM opened and fails silently, so a single
          // dropped response leaves the section blank until the page is reloaded.
          // Treat an empty DataSet as a failure too: a trends request answered
          // against another casino's database comes back 200 with zero rows. The
          // cost is one extra request for an EGM that genuinely has no trend data.
          map((resp) => {
            const rows = resp?.DataSet ?? [];
            if (rows.length === 0) throw new Error('empty trends response');
            return rows;
          }),
          retry({ count: 1, delay: 500 }),
          catchError(() => of([] as ITrendsReport[]))
        )
        .subscribe({
          next(rows) {
            setTrendsData(rows);
          },
          complete() {
            setIsTrendsLoading(false);
          },
        });
    },
    [casinoSvc, casinoId]
  );

  const debouncedFetchSlot = useMemo(
    () => debounce((slot: number) => fetchSlotData(slot), 500),
    [fetchSlotData]
  );

  useEffect(() => {
    setActiveTimeframeId(defaultTimeframeId);
    setSlotFetched(false);
    setChartInsights(DEFAULT_CHART_INSIGHTS);
    // Drop back to the default — a new EGM decides for itself whether it has
    // jackpots worth showing.
    setJackpotOverlayPref(null);
  }, [activeSlotNumber, defaultTimeframeId]);

  // Details graph no longer offers 1h — if a stale id 1 is selected, snap to 24h.
  useEffect(() => {
    if (activeTimeframeId === 1) setActiveTimeframeId(INSIGHT_DEFAULT_TIMEFRAME.id);
  }, [activeTimeframeId]);

  // The game card is sticky, so on its own it stays pinned for the whole scroll.
  // Tuck it away once it is stuck and the user keeps scrolling down, and slide
  // it back on the first upward scroll; at the top of the panel the card and the
  // Details header both sit in flow again.
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    let scroller: HTMLElement | null = root.parentElement;
    while (scroller && !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) {
      scroller = scroller.parentElement;
    }
    if (!scroller) return;
    const el = scroller;

    let lastScrollTop = el.scrollTop;
    const handleScroll = () => {
      const top = el.scrollTop;
      const delta = top - lastScrollTop;
      if (Math.abs(delta) < 4) return;
      lastScrollTop = top;

      if (top <= 0) {
        setIsSlotRowTucked(false);
        return;
      }
      // Bottom rubber-band reverse is an upward delta — untucking the sticky
      // card there expands content height under a bottom-pinned scrollTop and
      // stutters. Only untuck on a real upward scroll away from the end.
      const maxScroll = Math.max(0, el.scrollHeight - el.clientHeight);
      const nearBottom = maxScroll > 0 && top >= maxScroll - 100;
      if (delta < 0) {
        if (!nearBottom) setIsSlotRowTucked(false);
        return;
      }
      const wrap = slotRowWrapRef.current;
      if (!wrap) return;
      // Only tuck once the card has reached the top of the panel — before that
      // it is still scrolling away under its own steam.
      const isStuck = wrap.getBoundingClientRect().top <= el.getBoundingClientRect().top + 1;
      if (isStuck) setIsSlotRowTucked(true);
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsSlotRowTucked(false);
  }, [activeSlotNumber]);

  useEffect(() => {
    debouncedFetchSlot(activeSlotNumber);
    return () => debouncedFetchSlot.cancel();
  }, [activeSlotNumber, debouncedFetchSlot]);

  useEffect(() => {
    fetchGraphData(activeSlotNumber, activeTimeframeId);
  }, [activeSlotNumber, activeTimeframeId, fetchGraphData]);

  useEffect(() => {
    const subscription = fetchTrendsData(activeSlotNumber);
    return () => subscription?.unsubscribe();
  }, [activeSlotNumber, fetchTrendsData]);

  useEffect(() => {
    if (userId && casinoId) fetchUserFavorites(userId, String(casinoId));
  }, [fetchUserFavorites, userId, casinoId]);

  useEffect(() => {
    if (isPlaylistEnabled && userId && casinoId) fetchUserPlaylist(userId, String(casinoId));
  }, [fetchUserPlaylist, userId, casinoId, isPlaylistEnabled]);

  useEffect(() => {
    const el = tableAreaRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width) setTrendCellWidth(Math.floor(width / 6));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleBack = () => {
    clearSlotHistory();
    onBackSlotList();
  };

  const chartData = graphData.map((d) => {
    const moneyWon     = Number(d.money_won)     || 0;
    const jackpotValue = Number(d.jackpot_value) || 0;
    const spins        = Number(d.spins)         || 0;
    const jackpotCount = Number(d.jackpots)      || 0;
    return {
      bucket:      d.bucket as string,
      // null on buckets without a jackpot so the tooltip's filterNull drops the
      // overlay row entirely rather than reading "$0 | 0" on every hover.
      [JACKPOT_OVERLAY_KEY]: jackpotCount > 0 ? jackpotValue : null,
      money_won:   moneyWon,
      money_played: Number(d.money_played) || 0,
      jackpots:    jackpotCount,
      spins,
      // The API returns RTP as a fraction (0.95); the chart plots it in percent
      // units so the axis, the tooltip and the 100% break-even line share a scale.
      return:      (Number(d.return) || 0) * 100,
      jackpot_value: jackpotValue,
      avg_money_won_per_spin:   spins > 0 ? moneyWon / spins : 0,
      jackpot_value_per_spin:   spins > 0 ? jackpotValue / spins : 0,
      non_jkpt_won_per_spin:    spins > 0 ? (moneyWon - jackpotValue) / spins : 0,
    };
  });

  const jackpotBuckets = chartData.filter((d) => d.jackpots > 0).map((d) => d.bucket);
  // Hour vs day labels: casinoInsight tells us via bucketType; legacy falls
  // back to the timeframe (<= 24h is hourly).
  const effectiveBucketType: GraphBucketType =
    graphBucketType ?? (activeTimeframeId <= 4 ? 'hour' : 'day');
  const visibleBuckets = useMemo(() => {
    if (graphBuckets && graphBuckets.length > 0) return pickTickBuckets(graphBuckets);
    const seen = new Set<string>();
    const ordered: string[] = [];
    chartData.forEach((d) => {
      const label = formatBucketLabel(d.bucket, effectiveBucketType);
      if (!seen.has(label)) {
        seen.add(label);
        ordered.push(d.bucket);
      }
    });
    return pickTickBuckets(ordered);
  }, [chartData, graphBuckets, effectiveBucketType]);

  const xTickFormatter = useCallback(
    (bucket: string): string =>
      visibleBuckets.has(bucket) ? formatBucketLabel(bucket, effectiveBucketType) : '',
    [visibleBuckets, effectiveBucketType]
  );

  // Window is always N bars (missing zero-filled). Hide only when every bar is
  // a synthetic empty (no real casinoInsight docs in the window).
  const hasChartData =
    chartData.length > 0 &&
    graphData.some((d) => !(d as { missing?: boolean }).missing);

  const isSingleInsight = chartInsights[1] === null;
  const primaryCategory = chartInsights[0] ? getInsightCategory(chartInsights[0]) : 'dollars';
  const secondaryCategory = chartInsights[1] ? getInsightCategory(chartInsights[1]) : null;
  const hasDualAxes = !isSingleInsight && secondaryCategory !== null && primaryCategory !== secondaryCategory;
  const jackpotSlotIdx = chartInsights.findIndex((i) => i === InsightValueEnum.jackpot_value);

  // Slot holding the money insight the overlay borrows its scale from, or -1.
  const jackpotHostSlotIdx = chartInsights.findIndex(
    (i) => i !== null && JACKPOT_OVERLAY_HOST_INSIGHTS.has(i)
  );
  // The toggle is offered only when jackpots are not already a selected insight
  // and there is a money series for them to sit against.
  const canToggleJackpotOverlay = jackpotSlotIdx < 0 && jackpotHostSlotIdx >= 0;
  // An EGM that actually hit a jackpot in the selected timeframe shows it
  // without being asked; one that did not keeps the graph clean. Until the user
  // taps the toggle this tracks the timeframe, so moving to a quiet 4h window
  // drops the overlay and moving back brings it in.
  const jackpotOverlayDefaultsOn = jackpotBuckets.length > 0;
  const isJackpotOverlayOn =
    canToggleJackpotOverlay && (jackpotOverlayPref ?? jackpotOverlayDefaultsOn);
  // Jackpot bars follow the selected Jackpots insight when there is one and the
  // overlay's host money insight otherwise.
  const jackpotBarSlotIdx =
    jackpotSlotIdx >= 0 ? jackpotSlotIdx : isJackpotOverlayOn ? jackpotHostSlotIdx : -1;

  const primaryChartColor =
    CHART_INSIGHTS.find((ci) => ci.value === chartInsights[0])?.color ?? '#11e162';
  const hasThemedTimeTabs = THEMED_TIME_TAB_INSIGHTS.has(chartInsights[0] as InsightValueEnum);

  const slotData = selectedSlot
    ? withInsightRtp(convertToSlotData(selectedSlot.slotData), selectedSlot.slotData, parseValue)
    : null;
  const rawSlotData = selectedSlot?.slotData ?? null;

  // Per-axis data ceilings for auto-fit (jackpot dollars + RTP guides included so
  // controlled domains don't clip them on the initial view).
  const seriesDataMax = useCallback(
    (insight: InsightSlot): number => {
      if (!insight) return 0;
      let max = 0;
      for (const d of chartData) {
        const raw = d[insight as keyof typeof d];
        const n = typeof raw === 'number' ? raw : Number(raw) || 0;
        if (n > max) max = n;
      }
      if (insight === InsightValueEnum.return) {
        max = Math.max(max, 100, showPop ? Number(rawSlotData?.pop) || 0 : 0);
      }
      return max;
    },
    [chartData, rawSlotData?.pop, showPop]
  );
  const jackpotDataMax = useMemo(
    () => chartData.reduce((m, d) => Math.max(m, Number(d.jackpot_value) || 0), 0),
    [chartData]
  );
  const { leftDataMax, rightDataMax } = useMemo(() => {
    let left = seriesDataMax(chartInsights[0]);
    let right: number | null = null;
    if (hasDualAxes) {
      right = seriesDataMax(chartInsights[1]);
    } else if (chartInsights[1]) {
      left = Math.max(left, seriesDataMax(chartInsights[1]));
    }
    if (jackpotBarSlotIdx >= 0) {
      if (hasDualAxes) {
        if (jackpotBarSlotIdx === 0) left = Math.max(left, jackpotDataMax);
        else if (right != null) right = Math.max(right, jackpotDataMax);
      } else {
        left = Math.max(left, jackpotDataMax);
      }
    }
    return { leftDataMax: left, rightDataMax: right };
  }, [
    seriesDataMax,
    chartInsights,
    hasDualAxes,
    jackpotBarSlotIdx,
    jackpotDataMax,
  ]);

  const yZoomResetKey = [
    activeSlotNumber,
    activeTimeframeId,
    chartInsights[0],
    chartInsights[1],
    isJackpotOverlayOn ? 1 : 0,
  ].join('|');

  const {
    chartAreaProps,
    leftDomain,
    rightDomain,
    leftView,
    rightView,
    leftTicks,
    rightTicks,
    leftStep,
    rightStep,
    isAnyZoomed,
  } = useYAxisZoom({
    leftDataMax,
    rightDataMax,
    resetKey: yZoomResetKey,
  });

  const getTimeframeInsightValue = useCallback((insight: InsightValueEnum): number | null => {
    const row = trendsData.find((t) => t.insight === insight);
    if (!row) return null;
    const map: Partial<Record<number, number>> = {
      6: row.m_insight_value,
      5: row.w_insight_value,
      4: row.d_insight_value,
      3: row.h8_insight_value,
      2: row.h4_insight_value,
      1: row.h1_insight_value,
    };
    return map[activeTimeframeId] ?? null;
  }, [trendsData, activeTimeframeId]);

  // Additive insights: legend total = sum of bars in the selected window
  // (matches the chart). Rates / RTP stay on the trends summary for that window.
  const ADDITIVE_LEGEND_INSIGHTS = useMemo(
    () =>
      new Set<InsightValueEnum>([
        InsightValueEnum.money_won,
        InsightValueEnum.money_played,
        InsightValueEnum.spins,
        InsightValueEnum.jackpot_value,
        InsightValueEnum.jackpots,
      ]),
    []
  );

  const getLegendValue = useCallback((insight: InsightSlot): string => {
    if (!insight) return '- - - -';
    if (ADDITIVE_LEGEND_INSIGHTS.has(insight) && chartData.length > 0) {
      if (insight === InsightValueEnum.jackpot_value) {
        const dollars = chartData.reduce((s, d) => s + (Number(d.jackpot_value) || 0), 0);
        const count = chartData.reduce((s, d) => s + d.jackpots, 0);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        return `${String(parseValue(dollars, insight))} | ${Math.round(count)}`;
      }
      const sum = chartData.reduce((s, d) => s + (Number(d[insight as keyof typeof d]) || 0), 0);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      return String(parseValue(sum, insight));
    }
    const val = getTimeframeInsightValue(insight);
    if (val === null) return '- - - -';
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    return String(parseValue(val, insight));
  }, [getTimeframeInsightValue, chartData, ADDITIVE_LEGEND_INSIGHTS]);

  const handleTrendLabelClick = useCallback((insight: InsightValueEnum) => {
    setChartInsights((prev) => {
      if (prev[0] === insight) return prev;
      const newSecondary = prev[1] === insight ? prev[0] : prev[1];
      return [insight, newSecondary];
    });
  }, []);

  // The whole picker row is the hit target — tapping the label selects the
  // insight just like tapping the dot.
  const handlePickInsight = useCallback(
    (value: InsightSlot) => {
      setChartInsights((prev) => {
        if (pickingSlot === null) return prev;
        const next = [...prev] as [InsightSlot, InsightSlot];
        next[pickingSlot] = value;
        return next;
      });
      setPickingSlot(null);
    },
    [pickingSlot]
  );

  const isJackpotRow = Number(rawSlotData?.jackpots) > 0;
  // stats mirrors HotListReport: insightKey is 'return', so stats = return_ntile (fallback money_won_ntile)
  const stats =
    Number(rawSlotData?.return_ntile) || Number(rawSlotData?.money_won_ntile) || 3;

  const backgroundColor = getRowColor(stats);
  const isInsightCard = isInsightCardRow(rawSlotData);
  const cardMeta = egmCardMeta(rawSlotData);
  const menuBgColor = getMenuBackgroundColor(stats, Number(rawSlotData?.jackpots));
  const rowTextColor = getTextColor(stats);

  // rowStyle mirrors HotListReport — gradient tail for regular rows, coin bg for jackpot rows
  const rowGradientColor = isJackpotRow ? '#FFBD60' : getRowColor(stats);
  const rowBackground = `linear-gradient(90deg, rgba(29, 33, 46, 0) 0%, rgba(29, 33, 46, 0) 70%, ${getRowColorWithOpacity(rowGradientColor, 0.5)} 100%)`;
  const rowCardStyle: React.CSSProperties = {
    background: isJackpotRow
      ? `url("assets/images/coin-report.png") right center / auto 100% no-repeat, ${rowBackground} var(--BL-Styles-to-use-Transparent-blue-frame, #192538)`
      : `${rowBackground} var(--BL-Styles-to-use-Transparent-blue-frame, #192538)`,
  };

  const closeMenu = () => setPopoverPosition(null);

  const handleMenuAction = (action: 'favorite' | 'playlist' | 'filterTitle' | 'filterTheme') => {
    if (!rawSlotData) return;
    const slotNum = egmDetailKey(rawSlotData);
    if (!slotNum && (action === 'favorite' || action === 'playlist')) return;

    if (action === 'favorite') {
      if (userId && casinoId) {
        if (isIncludedInFavorites(slotNum, favoritesList)) {
          handleDeleteFavorite(slotNum, userId, String(casinoId), true);
        } else {
          handleInsertFavorite(slotNum, userId, String(casinoId), true);
        }
      }
    } else if (action === 'playlist') {
      if (userId && casinoId) {
        if (isIncludedInPlaylist(slotNum, playlist)) {
          handleDeletePlaylist(slotNum, userId, String(casinoId), true);
        } else {
          handleInsertPlaylist(slotNum, userId, String(casinoId), true);
        }
      }
    } else if (action === 'filterTitle' || action === 'filterTheme') {
      const data: ISearchInfo[] = (casinoSearchData as any)?.data ?? [];
      const titleKey = getCleanGameName(rawSlotData.game_name);
      const themeKey =
        (rawSlotData.game_theme && rawSlotData.game_theme.trim()) ||
        getCleanThemeName(rawSlotData.game_name);

      let filterName = '';
      let matches: ISearchInfo[] = [];
      if (action === 'filterTitle') {
        filterName = titleKey;
        matches = data.filter((d) => d.game_name === titleKey);
      } else {
        if (!themeKey) {
          closeMenu();
          return;
        }
        filterName = themeKey;
        matches = data.filter((d) => d.theme === themeKey);
      }
      const selfId = egmDetailId(rawSlotData);
      // Insight search rows: mnum. Legacy: slot_number. Never floorLocation.
      const slotNumbers = matches.length
        ? matches.map((m) => searchRowDetailId(m)).filter((n): n is number => n !== null)
        : selfId !== null
          ? [selfId]
          : [];

      slotCommands.SetSearchMode(true);
      slotCommands.SelectBankFilter(false);
      slotCommands.SelectedSlotsName(filterName);
      slotCommands.SelectedSlots(slotNumbers.map(String) as unknown as string[]);
    }

    closeMenu();
  };

  const slotNumberStr = rawSlotData ? egmDetailKey(rawSlotData) : slotData ? String(slotData.slotNumber) : '';

  return (
    <div className="dr-v2" ref={rootRef}>
      {/* ── Top bar: X close | ⓘ Details | rotate phone ── */}
      <div className="dr-v2-topbar">
        <button className="dr-v2-topbar-close" onClick={handleBack}>
          <IonIcon icon={close} />
        </button>
        <div className="dr-v2-topbar-title">
          <span className="dr-v2-topbar-title-text">Details</span>
        </div>
        <button className="dr-v2-topbar-layout">
          <ReactSVG src="assets/icon/rotate-phone.svg" className="dr-v2-rotate-svg" />
        </button>
      </div>

      {(isLoading || (!slotFetched && !slotData)) && (
        <div className="dr-v2-spinner">
          <div className="dr-v2-spinner-inner">
            <SVGComponent />
          </div>
        </div>
      )}

      {slotFetched && !slotData && !isLoading && (
        <div className="dr-v2-no-chart">No details available for this machine in this timeframe</div>
      )}

      {slotData && !isLoading && (
        <>
          {/* ── 1. Game Card — same slot-table-row design as HotListReport ── */}
          <div
            className={`dr-v2-slot-row-wrap${isSlotRowTucked ? ' dr-v2-slot-row-wrap--tucked' : ''}`}
            ref={slotRowWrapRef}
          >
            <div className="dr-v2-slot-row" style={rowCardStyle}>
              {isJackpotRow && <span className="jkpt-title">JACKPOT</span>}
              <div className="game-card-info">
                <div className="game-card__center">
                  <div className="game-card__info">
                    <TextMarquee
                      className="game-card__name"
                      text={
                        isInsightCard
                          ? insightCardName(rawSlotData)
                          : toCapitalizedWords(getCleanGameName(slotData.name))
                      }
                    />
                    {(() => {
                      const theme = isInsightCard
                        ? insightCardTitle(rawSlotData)
                        : (rawSlotData?.game_theme && rawSlotData.game_theme.trim()) ||
                          getCleanThemeName(slotData.name);
                      return theme ? (
                        <div className="game-card__theme">
                          {isInsightCard ? theme : toCapitalizedWords(theme)}
                        </div>
                      ) : null;
                    })()}
                    <div className="game-card__meta">
                      {cardMeta.number}
                      {cardMeta.showSeparator && <span className="dot-box">-</span>}
                      {cardMeta.bankLabel}
                      {isPlaylistEnabled && (
                        <span
                          role="button"
                          aria-label={
                            isIncludedInPlaylist(slotNumberStr, playlist)
                              ? 'Remove from Playlist'
                              : 'Add to Playlist'
                          }
                          onClick={() => {
                            if (userId && casinoId) {
                              if (isIncludedInPlaylist(slotNumberStr, playlist)) {
                                handleDeletePlaylist(slotNumberStr, userId, String(casinoId));
                              } else {
                                handleInsertPlaylist(slotNumberStr, userId, String(casinoId));
                              }
                            }
                          }}
                          className="dr-v2-meta-icon-btn"
                        >
                          <PlaylistIcon
                            size={16}
                            className="playlist-icon"
                            filled={isIncludedInPlaylist(slotNumberStr, playlist)}
                          />
                        </span>
                      )}
                      <span
                        role="button"
                        aria-label={
                          isIncludedInFavorites(slotNumberStr, favoritesList)
                            ? 'Remove from Favorites'
                            : 'Add to Favorites'
                        }
                        onClick={() => {
                          if (userId && casinoId) {
                            if (isIncludedInFavorites(slotNumberStr, favoritesList)) {
                              handleDeleteFavorite(slotNumberStr, userId, String(casinoId));
                            } else {
                              handleInsertFavorite(slotNumberStr, userId, String(casinoId));
                            }
                          }
                        }}
                        className="dr-v2-meta-icon-btn"
                      >
                        <FavoriteIcon
                          className="favorite-icon"
                          filled={isIncludedInFavorites(slotNumberStr, favoritesList)}
                        />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="hot-list-row list-portrait">
                <div className="game-data-td-report">
                  <span
                    className="row-insight-value"
                    style={{ background: backgroundColor, color: rowTextColor }}
                  >
                    <div className="row-insight-value__text">{slotData.rtp}</div>
                  </span>
                </div>
                <IonIcon
                  icon={ellipsisVertical}
                  className="menu-icon"
                  style={{ color: menuBgColor }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const rect = (e.currentTarget as unknown as HTMLElement).getBoundingClientRect();
                    const menuItemCount = isPlaylistEnabled ? 4 : 3;
                    const popoverHeight = menuItemCount * 48 + 2 * 13 + 16;
                    const safeMargin = 16;
                    let x = rect.left - MACHINE_MENU_POPOVER_WIDTH - 4;
                    if (x < safeMargin) x = safeMargin;
                    let y = rect.top;
                    if (y + popoverHeight + safeMargin > window.innerHeight) {
                      y = rect.bottom - popoverHeight;
                      if (y < safeMargin) y = safeMargin;
                    }
                    setPopoverPosition({ x, y });
                  }}
                />
                <span
                  className="menu-icon-bubble"
                  style={{ backgroundColor: menuBgColor }}
                />
              </div>
            </div>
          </div>

          {/* ── 2. Graph ── */}
          <div className="dr-v2-chart-card">
            

            {/* Chart legend — 2 selectable insight slots */}
            <div className="dr-v2-chart-legend">
              {chartInsights.map((insight, idx) => {
                const meta = CHART_INSIGHTS.find((ci) => ci.value === insight);
                const isEmpty = !insight;
                return (
                  <div
                    key={idx}
                    className={`dr-v2-legend-row${idx > 0 && isEmpty ? ' dr-v2-legend-row--secondary' : ''}`}
                  >
                    <div className="dr-v2-legend-row-start">
                      <button
                        className={`dr-v2-legend-item${isEmpty ? ' dr-v2-legend-item--empty' : ''}`}
                        data-insight={!isEmpty ? insight : undefined}
                        onClick={() => setPickingSlot(idx as 0 | 1)}
                      >
                        <span className="dr-v2-legend-item-label">
                          {isEmpty ? '+ Insight' : meta?.label}
                        </span>
                      </button>
                      {idx === 0 && (
                        <button
                          className="dr-v2-swap-btn"
                          onClick={() =>
                            setChartInsights(([a, b]) => [b ?? a, b === null ? null : a])
                          }
                        >
                          <IonIcon icon={swapVerticalOutline} />
                        </button>
                      )}
                      {idx === 0 && canToggleJackpotOverlay && (
                        <button
                          className={`dr-v2-swap-btn dr-v2-jkpt-toggle${isJackpotOverlayOn ? ' dr-v2-jkpt-toggle--on' : ''}`}
                          aria-pressed={isJackpotOverlayOn}
                          aria-label={
                            isJackpotOverlayOn ? 'Hide jackpots on graph' : 'Show jackpots on graph'
                          }
                          onClick={() => setJackpotOverlayPref(!isJackpotOverlayOn)}
                        >
                          <JackpotStackIcon />
                        </button>
                      )}
                    </div>
                    {(() => {
                      const legendVal = isEmpty ? '- - - -' : getLegendValue(insight);
                      const isDashes = legendVal === '- - - -';
                      return (
                        <span
                          className="dr-v2-legend-value"
                          data-insight={!isEmpty && !isDashes ? insight : undefined}
                          style={isEmpty || isDashes ? { color: isDashes ? 'rgba(255,255,255,0.35)' : '#ffffff' } : undefined}
                        >
                          {legendVal}
                        </span>
                      );
                    })()}
                  </div>
                );
              })}
            </div>

            {/* Time tabs — active tab color follows primary insight */}
            <div
              className="dr-v2-time-tabs"
              data-primary-insight={hasThemedTimeTabs ? String(chartInsights[0]) : undefined}
            >
              {timeTabs.map((tab) => (
                <button
                  key={tab.value}
                  className={`dr-v2-tab${activeTimeframeId === tab.timeframeId ? ' active' : ''}`}
                  style={
                    activeTimeframeId === tab.timeframeId && !hasThemedTimeTabs
                      ? { background: primaryChartColor, color: '#fff' }
                      : undefined
                  }
                  onClick={() => setActiveTimeframeId(tab.timeframeId)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Area chart — primary = line, secondary = faded gradient fill.
                Axis strips always capture vertical drags for Y zoom; the plot
                only captures them while zoomed (see --zoomed + touch-action). */}
            <div
              className={`dr-v2-chart-area${isAnyZoomed ? ' dr-v2-chart-area--zoomed' : ''}`}
              {...chartAreaProps}
            >
              {isGraphLoading ? (
                <div className="dr-v2-chart-loading">
                  <IonSpinner name="crescent" />
                </div>
              ) : hasChartData ? (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart
                    data={chartData}
                    margin={{ top: 8, right: hasDualAxes ? 27 : 12, bottom: 4, left: 4 }}
                  >
                    <defs>
                      {/* Gold gradient for jackpot bars — matches explorer tier-top pill */}
                      <linearGradient id="goldTrimBarGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#fffea5" />
                        <stop offset="50%" stopColor="#FFD700" />
                        <stop offset="100%" stopColor="#f4b942" />
                      </linearGradient>
                      {/* Stroke gradients for each insight */}
                      <linearGradient id="drv2RtpLineGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#F9379D" />
                        <stop offset="100%" stopColor="#58D1FE" />
                      </linearGradient>
                      <linearGradient id="drv2WonGrad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#11E162" />
                        <stop offset="100%" stopColor="#0DEAC0" />
                      </linearGradient>
                      <linearGradient id="drv2PlayedGrad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#FF3D51" />
                        <stop offset="100%" stopColor="#FF6060" />
                      </linearGradient>
                      <linearGradient id="drv2SpinsGrad" x1="1" y1="0" x2="0" y2="0">
                        <stop offset="1.7%" stopColor="#C5CED4" />
                        <stop offset="100%" stopColor="#FFFFFF" />
                      </linearGradient>
                      {/* $ W/S uses same gradient as $ Won */}
                      <linearGradient id="drv2WsGrad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#11E162" />
                        <stop offset="100%" stopColor="#0DEAC0" />
                      </linearGradient>
                      {/* J W/S uses gold gradient matching explorer tier-top pill */}
                      <linearGradient id="drv2JwsGrad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#FFD700" />
                        <stop offset="100%" stopColor="#fffea5" />
                      </linearGradient>
                      {/* Area fill gradients */}
                      {chartInsights.map((insight, idx) => {
                        if (!insight) return null;
                        const color =
                          CHART_INSIGHTS.find((ci) => ci.value === insight)?.color ?? '#fff';
                        return (
                          <linearGradient key={insight} id={`drv2Grad${idx}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={color} stopOpacity={idx === 0 ? 0.25 : 0.15} />
                            <stop offset="95%" stopColor={color} stopOpacity={0.02} />
                          </linearGradient>
                        );
                      })}
                    </defs>
                    <XAxis
                      dataKey="bucket"
                      tickFormatter={xTickFormatter}
                      tick={{ fontSize: 10, fill: '#aaa' }}
                      interval={0}
                      axisLine={false}
                      tickLine={false}
                    />
                    {hasDualAxes ? (
                      <>
                        <YAxis
                          yAxisId="left"
                          domain={leftDomain}
                          ticks={leftTicks}
                          tickFormatter={(v: number) => formatYAxisTick(v, primaryCategory, leftStep)}
                          tick={{ fontSize: 10, fill: '#aaa' }}
                          axisLine={false}
                          tickLine={false}
                          width={40}
                          allowDataOverflow
                        />
                        <YAxis
                          yAxisId="right"
                          orientation="right"
                          domain={rightDomain}
                          ticks={rightTicks}
                          tickFormatter={(v: number) =>
                            formatYAxisTick(v, secondaryCategory ?? 'dollars', rightStep)
                          }
                          tick={{ fontSize: 10, fill: '#aaa' }}
                          axisLine={false}
                          tickLine={false}
                          width={27}
                          allowDataOverflow
                        />
                      </>
                    ) : (
                      <YAxis
                        domain={leftDomain}
                        ticks={leftTicks}
                        tickFormatter={(v: number) => formatYAxisTick(v, primaryCategory, leftStep)}
                        tick={{ fontSize: 10, fill: '#aaa' }}
                        axisLine={false}
                        tickLine={false}
                        width={40}
                        allowDataOverflow
                      />
                    )}
                    <Tooltip
                      formatter={(
                        value: number,
                        name: string,
                        item: { payload?: { jackpots?: number } }
                      ) => {
                        // The overlay has no insight slot of its own, so it reads
                        // out under the Jackpots label and formatting.
                        const insightName =
                          name === JACKPOT_OVERLAY_KEY ? InsightValueEnum.jackpot_value : name;
                        const ci = CHART_INSIGHTS.find((c) => c.value === insightName);
                        if (!ci) return [String(value), name];
                        const formatted = formatTooltipValue(value, insightName as InsightValueEnum);
                        // Jackpot dollars are a sum for the bucket — show how many
                        // jackpots make it up, same "$40,459 | 6" format as the legend.
                        if (insightName === InsightValueEnum.jackpot_value) {
                          const count = Math.round(Number(item?.payload?.jackpots ?? 0));
                          return [`${formatted} | ${count}`, ci.label];
                        }
                        return [formatted, ci.label];
                      }}
                      labelFormatter={(label) => formatXLabel(label as string, activeTimeframeId)}
                      contentStyle={{
                        background: '#1e2328',
                        border: 'none',
                        color: '#fff',
                        fontSize: 12,
                        borderRadius: 6,
                      }}
                    />
                    {/* RTP reference lines — 100% break-even + POP (clipped to the zoomed view) */}
                    {(() => {
                      if (!chartInsights.some((i) => i === InsightValueEnum.return)) return null;
                      const rtpAxisId = hasDualAxes
                        ? (chartInsights[0] === InsightValueEnum.return ? 'left' : 'right')
                        : undefined;
                      const rtpView = rtpAxisId === 'right' ? rightView : leftView;
                      return (
                        <>
                          {isYInView(100, rtpView) && (
                            <ReferenceLine
                              y={100}
                              stroke="rgba(255,255,255,0.45)"
                              strokeDasharray="4 3"
                              strokeWidth={1}
                              {...(rtpAxisId ? { yAxisId: rtpAxisId } : {})}
                            />
                          )}
                          {showPop && rawSlotData?.pop != null && isYInView(rawSlotData.pop, rtpView) && (
                            <ReferenceLine
                              y={rawSlotData.pop}
                              stroke="#F4B942"
                              strokeDasharray="4 3"
                              strokeWidth={1}
                              {...(rtpAxisId ? { yAxisId: rtpAxisId } : {})}
                            />
                          )}
                        </>
                      );
                    })()}
                    {/* Jackpot bars — gold-trim.png pattern, value-scaled against the
                        selected Jackpots insight or, for the overlay, the money insight.
                        Bars taller than the zoomed view are capped with a ▲. */}
                    {/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unnecessary-type-assertion */}
                    <Customized
                      component={({ xAxisMap, yAxisMap }: any) => {
                        if (jackpotBarSlotIdx < 0 || !jackpotBuckets.length) return null;
                        type RAxis = { scale: (v: unknown) => number; y: number; height: number };
                        type RAxisMap = Record<string, RAxis>;
                        const xAxis = xAxisMap
                          ? (Object.values(xAxisMap as RAxisMap)[0] as RAxis | undefined)
                          : null;
                        const yAxisKey = hasDualAxes
                          ? (jackpotBarSlotIdx === 0 ? 'left' : 'right')
                          : Object.keys((yAxisMap ?? {}) as RAxisMap)[0];
                        const yAxis = (yAxisMap as RAxisMap | undefined)?.[yAxisKey]
                          ?? (yAxisMap ? Object.values(yAxisMap as RAxisMap)[0] : null) as RAxis | null;
                        if (!xAxis?.scale || yAxis?.y === undefined) return null;
                        const chartTop = Number(yAxis.y);
                        const chartBottom = Number(yAxis.y) + Number(yAxis.height);
                        const jackpotView =
                          hasDualAxes && jackpotBarSlotIdx === 1 ? rightView : leftView;
                        return (
                          <>
                            {jackpotBuckets.map((bucket, i) => {
                              const xPos = Number(xAxis.scale(bucket));
                              if (isNaN(xPos)) return null;
                              const dp = chartData.find((d) => d.bucket === bucket);
                              const jVal = Number(dp?.jackpot_value ?? 0);
                              const { y: yTop, height: barH, capped } = jackpotBarLayout(
                                jVal,
                                jackpotView,
                                chartTop,
                                chartBottom
                              );
                              return (
                                <g key={i}>
                                  <rect
                                    x={xPos - 3}
                                    y={yTop}
                                    width={6}
                                    height={barH}
                                    fill="url(#goldTrimBarGrad)"
                                    opacity={0.85}
                                  />
                                  {capped && (
                                    <text
                                      x={xPos}
                                      y={chartTop + 9}
                                      textAnchor="middle"
                                      fill="#FFD700"
                                      fontSize={9}
                                      fontWeight={700}
                                    >
                                      ▲
                                    </text>
                                  )}
                                </g>
                              );
                            })}
                          </>
                        );
                      }}
                    />
                    {/* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument */}
                    {/* Render insight lines from back to front (slot 1 behind slot 0) */}
                    {[...chartInsights].reverse().map((insight, reversedIdx) => {
                      if (!insight) return null;
                      const idx = 1 - reversedIdx;
                      const isJackpot = insight === InsightValueEnum.jackpot_value;
                      const color =
                        CHART_INSIGHTS.find((ci) => ci.value === insight)?.color ?? '#fff';
                      const gradId = INSIGHT_STROKE_GRADIENTS[insight];
                      const strokeColor = gradId ? `url(#${gradId})` : color;
                      const yAxisId = hasDualAxes ? (idx === 0 ? 'left' : 'right') : undefined;
                      return (
                        <Area
                          key={insight}
                          type="monotone"
                          dataKey={insight}
                          stroke={isJackpot ? 'none' : strokeColor}
                          strokeWidth={isJackpot ? 0 : (idx === 0 ? 2 : 1.5)}
                          fill={isJackpot ? 'none' : `url(#drv2Grad${idx})`}
                          fillOpacity={isJackpot ? 0 : 1}
                          dot={false}
                          isAnimationActive={false}
                          {...(yAxisId ? { yAxisId } : {})}
                        />
                      );
                    })}
                    {/* Overlay carrier — draws nothing, but puts the bucket's jackpot
                        dollars in the tooltip and widens the money axis so a tall
                        jackpot bar is not clipped at the top of the plot. */}
                    {isJackpotOverlayOn && (
                      <Area
                        dataKey={JACKPOT_OVERLAY_KEY}
                        stroke="none"
                        fill="none"
                        fillOpacity={0}
                        dot={false}
                        activeDot={false}
                        connectNulls={false}
                        isAnimationActive={false}
                        {...(hasDualAxes
                          ? { yAxisId: jackpotHostSlotIdx === 0 ? 'left' : 'right' }
                          : {})}
                      />
                    )}
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <div className="dr-v2-no-chart">No chart data for this timeframe yet</div>
              )}
            </div>
          </div>

          {/* ── 3. Trends ── */}
          <div className="dr-v2-trends">
            <SectionHeader title="Trends" showRow={true} iconName="arrow" />
            {isTrendsLoading ? (
              <div className="dr-v2-trends-loading">
                <IonSpinner name="crescent" />
              </div>
            ) : trendsData.length === 0 ? (
              <div className="dr-v2-no-chart">No trend data available for this machine yet</div>
            ) : (
              <div className="dr-v2-trends-scroll-wrap">
                <div className="dr-v2-trends-labels">
                  <div className="dr-v2-trends-label-spacer" />
                  {V2_TREND_ROWS.map(({ insight, label }) => {
                    const row = trendsData.find((t) => t.insight === insight);
                    if (!row) return null;
                    return (
                      <div
                        key={insight}
                        className="dr-v2-trends-row-label"
                        onClick={() => handleTrendLabelClick(insight)}
                      >
                        {label}
                      </div>
                    );
                  })}
                </div>
                <div className="dr-v2-trends-table-area" ref={tableAreaRef}>
                  {/* Header */}
                  <div className="dr-v2-trends-row dr-v2-trends-header-row">
                    <div className="dr-v2-trends-cell">30 Days</div>
                    <div className="dr-v2-trends-cell">7 Days</div>
                    <div className="dr-v2-trends-cell">24 Hr</div>
                    <div className="dr-v2-trends-cell">8 Hr</div>
                    <div className="dr-v2-trends-cell">4 Hr</div>
                    <div className="dr-v2-trends-cell">1 Hr</div>
                  </div>
                  {/* Data rows */}
                  {V2_TREND_ROWS.map(({ insight }) => {
                    const row = trendsData.find((t) => t.insight === insight);
                    if (!row) return null;
                    const cols = [
                      { value: row.m_insight_value, ntile: row.m_insight_ntile },
                      { value: row.w_insight_value, ntile: row.w_insight_ntile },
                      { value: row.d_insight_value, ntile: row.d_insight_ntile },
                      { value: row.h8_insight_value, ntile: row.h8_insight_ntile },
                      { value: row.h4_insight_value, ntile: row.h4_insight_ntile },
                      { value: row.h1_insight_value, ntile: row.h1_insight_ntile },
                    ];
                    const isReturnInsight = insight === InsightValueEnum.return;
                    const trendValues = isReturnInsight
                      ? {
                        monthTrendValue: {
                          value: row.m_insight_value,
                          ntile: row.m_insight_ntile,
                        },
                        weekTrendValue: {
                          value: row.w_insight_value,
                          ntile: row.w_insight_ntile,
                        },
                        dayTrendValue: { value: row.d_insight_value, ntile: row.d_insight_ntile },
                        eightHoursTrendValue: {
                          value: row.h8_insight_value,
                          ntile: row.h8_insight_ntile,
                        },
                        fourHoursTrendValue: {
                          value: row.h4_insight_value,
                          ntile: row.h4_insight_ntile,
                        },
                        oneHourTrendValue: {
                          value: row.h1_insight_value,
                          ntile: row.h1_insight_ntile,
                        },
                      }
                      : null;
                    return (
                      <div key={insight} className="dr-v2-trends-row">
                        {isReturnInsight && trendValues && (
                          <GradientLineChart
                            trendValues={trendValues}
                            keyName={insight}
                            screenWidth={trendCellWidth}
                            isLandscape={false}
                            insightType={insight}
                            visibleInsights={[InsightValueEnum.return]}
                            postionLeft={4}
                          />
                        )}
                        {cols.map((col, i) => {
                          const cellValue = parseValueForTrends(col.value, insight);
                          const isEmptyCell = cellValue === TRENDS_EMPTY_VALUE;
                          return (
                            <div
                              key={i}
                              className={`dr-v2-trends-cell dr-v2-trends-val-cell${isEmptyCell ? ' dr-v2-trends-val-cell--empty' : ''}`}
                            >
                              <span
                                className="dr-v2-ntile-dot"
                                style={{ background: getHeatColor(col.ntile) }}
                              />
                              {cellValue}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ── 4. Game Information ── */}
          {selectedSlot && <GameInformation casinoReport={selectedSlot.slotData} />}
        </>
      )}

      {/* Insight picker popup — portal to body, matches SettingsInline design */}
      {pickingSlot !== null &&
        createPortal(
          <>
            <div className="settings-inline-backdrop" onClick={() => setPickingSlot(null)} />
            <div className="settings-inline" onClick={(e) => e.stopPropagation()}>
              <div className="settings-inline__header">
                <span className="si-header-spacer" />
                <span className="settings-title">
                  {pickingSlot === 0 ? 'Primary Insight' : 'Secondary Insight'}
                </span>
                <span className="si-header-spacer" />
              </div>

              <div className="si-divider" />

              <div className="si-insight-section">
                <div className="si-insight-list">
                  {CHART_INSIGHTS.filter((ci) => {
                    const otherSlot = pickingSlot === 0 ? 1 : 0;
                    return chartInsights[otherSlot] !== ci.value;
                  }).map((ci) => {
                    const isSelected = chartInsights[pickingSlot] === ci.value;
                    return (
                      <button
                        key={ci.value}
                        type="button"
                        className={`si-insight-row si-insight-row--btn${isSelected ? ' si-insight-row--selected' : ''}`}
                        onClick={() => handlePickInsight(ci.value)}
                        aria-pressed={isSelected}
                        aria-label={`Select ${ci.label}`}
                      >
                        <span
                          className={`si-dot${isSelected ? ' si-dot--filled' : ' si-dot--empty'}`}
                        />
                        <span className="si-insight-label">{ci.label}</span>
                      </button>
                    );
                  })}
                  {pickingSlot === 1 && (
                    <button
                      type="button"
                      className={`si-insight-row si-insight-row--btn${chartInsights[1] === null ? ' si-insight-row--selected' : ''}`}
                      onClick={() => handlePickInsight(null)}
                      aria-pressed={chartInsights[1] === null}
                      aria-label="Select None"
                    >
                      <span
                        className={`si-dot${chartInsights[1] === null ? ' si-dot--filled' : ' si-dot--empty'}`}
                      />
                      <span className="si-insight-label">None</span>
                    </button>
                  )}
                </div>
              </div>

              <div className="si-divider" />

              <div className="dr-v2-insight-popup__presets">
                {INSIGHT_PRESETS.map((preset, presetIdx) => {
                  const isActive =
                    chartInsights[0] === preset.primary && chartInsights[1] === preset.secondary;
                  return (
                    <button
                      key={preset.label}
                      className={`dr-v2-insight-popup__preset dr-v2-insight-popup__preset--${presetIdx}${isActive ? ' active' : ''}`}
                      style={presetIdx === 0 ? { ['--preset-color' as any]: preset.color } : undefined}
                      onClick={() => {
                        setChartInsights([preset.primary, preset.secondary]);
                        setPickingSlot(null);
                      }}
                    >
                      {preset.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </>,
          document.body
        )}

      {popoverPosition &&
        rawSlotData &&
        createPortal(
          <>
            <div onClick={closeMenu} style={machineMenuBackdropStyle} />
            <div
              role="menu"
              className="machine-menu-actions"
              onClick={(e) => e.stopPropagation()}
              style={{
                ...machineMenuContainerStyle,
                left: popoverPosition.x,
                top: popoverPosition.y,
              }}
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => handleMenuAction('favorite')}
                style={machineMenuItemStyle}
              >
                <span style={machineMenuIconSlotStyle} aria-hidden="true">
                  <FavoriteIcon variant="menu" />
                </span>
                {isIncludedInFavorites(egmDetailKey(rawSlotData), favoritesList)
                  ? 'Remove from Favorites'
                  : 'Save to Favorites'}
              </button>
              {isPlaylistEnabled && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => handleMenuAction('playlist')}
                  style={machineMenuItemStyle}
                >
                  <span style={machineMenuIconSlotStyle} aria-hidden="true">
                    <PlaylistIcon filled size={14} color="#94A0AA" />
                  </span>
                  {isIncludedInPlaylist(egmDetailKey(rawSlotData), playlist)
                    ? 'Remove from Playlist'
                    : 'Save to Playlist'}
                </button>
              )}
              {(() => {
                const hasTitle = !!getCleanGameName(rawSlotData.game_name);
                const hasTheme = !!(
                  (rawSlotData.game_theme && rawSlotData.game_theme.trim()) ||
                  getCleanThemeName(rawSlotData.game_name)
                );
                if (!hasTitle && !hasTheme) return null;
                return (
                  <>
                    <hr style={machineMenuDividerStyle} />
                    {hasTitle && (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => handleMenuAction('filterTitle')}
                        style={machineMenuFilterItemStyle}
                      >
                        <span style={machineMenuIconSlotStyle} aria-hidden="true">
                          <span
                            style={{
                              width: 16,
                              height: 16,
                              position: 'relative',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                            >
                              <circle
                                cx="8"
                                cy="8"
                                r="7.46667"
                                stroke="white"
                                strokeWidth="1.06667"
                              />
                            </svg>
                            <span
                              style={{
                                position: 'absolute',
                                color: '#FFF',
                                textAlign: 'center',
                                fontFamily: 'Rufina, serif',
                                fontSize: 11.635,
                                fontWeight: 400,
                                lineHeight: 'normal',
                              }}
                            >
                              T
                            </span>
                          </span>
                        </span>
                        Filter by Title
                      </button>
                    )}
                    {hasTheme && (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => handleMenuAction('filterTheme')}
                        style={machineMenuFilterItemStyle}
                      >
                        <span style={machineMenuIconSlotStyle} aria-hidden="true">
                          <span
                            style={{
                              width: 20,
                              height: 20,
                              borderRadius: 6,
                              border: '1px solid rgba(255, 255, 255, 0.70)',
                            }}
                          />
                        </span>
                        Filter by Theme
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          </>,
          document.body
        )}

    </div>
  );
};

export default DetailReportV2;
